import { $, alcance, aviso, avisoHtml, esc, fecha, limpiar, pedir, puede, rpc, rpcLee, sb, tengoRol } from './nucleo';

// =====================================================================
//  AJUSTES DEL SISTEMA  (0037 / 0038, punto 6)
// =====================================================================
//  Configuración que un admin cambia sin llamar a nadie. Hoy sólo el corte
//  del día.
//
//  Acá no se decide nada: formato de la hora, quién puede cambiarla y qué
//  pasa con un corte sin salida lo decide `fijar_corte` en la base (D67).
//  La pantalla manda lo escrito y muestra lo que la base contestó.
//
//  Este archivo es el dueño del corte para toda la app: Pedidos usa
//  `avisarCorte()` para avisar ANTES de cargar el pedido.
let CORTE = null, HIST_CORTE = [], PERSONAS = [];
const nombreDe = id => id == null ? '—'
  : (PERSONAS.find(p => p.id === id)?.nombre || 'alguien que ya no está activo');

// ---------------------------------------------------------------------
//  El estado del corte, que lo usan dos pantallas
// ---------------------------------------------------------------------
//  Una sola función lee la RPC: Pedidos y Ajustes no pueden discrepar.
export async function estadoDelCorte(){
  const { data, error } = await rpcLee('estado_del_corte');
  if (error) return null;
  CORTE = data;
  return data;
}

//  Aviso previo a cargar un pedido. No frena nada (la puerta está en la
//  base, D11): sólo anticipa lo que va a pasar.
export async function avisarCorte(selector){
  const el = $(selector);
  if (!el) return;
  const c = await estadoDelCorte();
  if (!c || !c.hay_corte){ el.innerHTML = ''; return; }

  if (!c.paso){
    el.innerHTML = `<div class="aviso a-info">El corte del día es a las
      <b>${esc(c.hora)}</b> y ahora son las ${esc(c.ahora)}. Después de esa hora,
      ${c.frena_carga ? 'cargar un pedido nuevo y anular' : 'anular'} pide autorización${
      tengoRol('admin_global') ? ' (a vos no: como admin global pasás directo)' : ''}.</div>`;
    return;
  }
  //  El admin global no pide autorización (0100, D219): la base lo deja
  //  pasar y anota el pase en Aprobaciones. Se pregunta por el CÓDIGO del rol.
  if (tengoRol('admin_global')){
    el.innerHTML = `<div class="aviso a-info">Ya pasó el corte de las
      <b>${esc(c.hora)}</b> (son las ${esc(c.ahora)}). Como sos admin global,
      cargás y anulás directo, sin pedir autorización: queda anotado en
      Aprobaciones.</div>`;
    return;
  }
  //  Ya pasó. Se aclara que entregar, empaquetar, despachar, cobrar y cerrar
  //  la caja no los frena el corte.
  el.innerHTML = `<div class="aviso a-warn">Ya pasó el corte de las
    <b>${esc(c.hora)}</b> (son las ${esc(c.ahora)}).
    ${c.frena_carga
      ? 'Cargar un pedido ahora necesita autorización: al confirmarlo te aparece «Pedir autorización» ahí mismo, y cuando te la den lo volvés a confirmar.'
      : 'Cargar sigue habilitado; anular es lo que pide autorización.'}
    ${c.puede_pedir_permiso ? '' :
      '<br><b>Y el permiso para pedirla está desactivado</b>, así que hoy no hay forma de destrabarlo: avisale a un administrador.'}
    Entregar, empaquetar, despachar, cobrar y cerrar la caja siguen igual.</div>`;
}

// ---------------------------------------------------------------------
//  La pantalla
// ---------------------------------------------------------------------
//  Si lo mostrado es sólo lo propio. La fija `cargarAjustes` (donde se pregunta
//  el alcance) y la lee el texto del caso vacío.
let SOLO_LO_MIO = false;

export async function cargarAjustes(){
  ['#corteMsg','#histCorteMsg'].forEach(limpiar);

  const configura = puede('config','administrar');
  //  El historial sale de `auditoria`, con su propio permiso (0005): quien
  //  configura no necesariamente lo puede leer, y al revés.
  const veHistorial = puede('auditoria','ver');
  //  Y CUÁNTO ve (D85). Con alcance `todos` el RLS de `auditoria` deja pasar
  //  todo; con otro, sólo las filas propias (`fijar_corte` cuenta como propia).
  //  Una lista parcial que no se anuncia se lee como completa, y "todavía
  //  nadie" pasaría a ser mentira: por eso lo dicen la tarjeta y el vacío.
  const TODO_EL_HISTORIAL = alcance('auditoria','ver') === 'todos';
  $('#cardCorte').classList.toggle('oculto', !configura);
  $('#cardHistCorte').classList.toggle('oculto', !veHistorial);
  SOLO_LO_MIO = veHistorial && !TODO_EL_HISTORIAL;

  const tareas: Promise<unknown>[] = [estadoDelCorte()];
  //  El historial guarda el id, no el nombre: hace falta la lista de personas.
  if (veHistorial) tareas.push(cargarHistorialCorte(), cargarPersonas());
  await Promise.all(tareas);

  if (configura) pintarCorte();
  if (veHistorial) pintarHistorialCorte();
  //  Ojo: el aviso del alcance va DESPUÉS de la lectura y mira si salió; el
  //  alcance explica una lista que existe, no una que no llegó.
  $('#histCorteAlcance').classList.toggle('oculto',
    !veHistorial || TODO_EL_HISTORIAL || !!ERROR_HIST);
}

//  Un historial que falla no se muestra como "todavía nadie cambió nada":
//  en auditoría esa frase es una respuesta falsa.
let ERROR_HIST = null;
async function cargarHistorialCorte(){
  ERROR_HIST = null;
  const { data, error } = await pedir(sb.from('auditoria')
    .select('id,accion,datos_antes,datos_despues,ocurrido_en,usuario_id')
    .eq('tabla','config').eq('accion','fijar_corte')
    .order('id', { ascending: false }).limit(50));
  if (error){ HIST_CORTE = []; ERROR_HIST = error.message; return; }
  HIST_CORTE = data || [];
}
async function cargarPersonas(){
  const { data, error } = await pedir(sb.from('usuarios').select('id,nombre')
    .eq('activo', true).order('nombre'));
  PERSONAS = error ? [] : (data || []);
}

function pintarCorte(){
  const c = CORTE;
  if (!c){ aviso('#corteMsg','bad','No se pudo leer el estado del corte.'); return; }

  //  La hora de "ahora" es la de la EMPRESA, no la del navegador: la calcula
  //  la base con `empresa.zona_horaria` (D39), y por eso se dice cuál es.
  $('#corteEstado').innerHTML = !c.hay_corte
    ? `<div class="aviso a-info">Hoy <b>no hay hora de corte</b>: no se frena ni
         cargar ni anular, a ninguna hora. Es como arranca una instalación nueva.</div>`
    : `<div class="aviso ${c.paso ? 'a-warn' : 'a-ok'}">El corte es a las
         <b>${esc(c.hora)}</b> y son las <b>${esc(c.ahora)}</b>
         (reloj de <span class="mono">${esc(c.zona)}</span>):
         ${c.paso ? 'ya pasó' : 'todavía no pasó'}.
         ${c.frena_carga
            ? 'Después de esa hora, cargar un pedido y anular piden autorización.'
            : 'Después de esa hora sólo anular pide autorización; cargar no se frena.'}
       </div>${c.puede_pedir_permiso ? '' :
         `<div class="aviso a-bad">El permiso <b>"Cargar o anular después del corte"</b>
            está desactivado. Con el corte prendido, después de las ${esc(c.hora)}
            nadie va a poder pedir autorización.</div>`}`;

  //  `hora` puede venir con segundos: `type="time"` no lo acepta y quedaría
  //  vacío en silencio.
  $('#ctHora').value  = c.hay_corte ? String(c.hora).slice(0,5) : '';
  $('#ctFrena').checked = !!c.frena_carga;
  $('#btnQuitarCorte').classList.toggle('oculto', !c.hay_corte);
}

function pintarHistorialCorte(){
  const hora = v => (v && v.hora) ? esc(v.hora) : '<span class="hint">sin corte</span>';
  $('#tblHistCorte').innerHTML = HIST_CORTE.map(h => `
    <tr><td>${fecha(h.ocurrido_en)}</td>
        <td>${hora(h.datos_antes)}</td>
        <td><b>${hora(h.datos_despues)}</b></td>
        <td>${!h.datos_despues?.hora ? '—'
              : h.datos_despues.frena_carga
              ? '<span class="chip c-warn">frena cargar</span>'
              : '<span class="chip">no frena cargar</span>'}</td>
        <td>${esc(h.datos_despues?.motivo || '—')}</td>
        <td>${esc(nombreDe(h.usuario_id))}</td></tr>
  `).join('') || `<tr><td colspan="6" class="vacio">${
      ERROR_HIST ? 'No se pudo leer el historial'
      : SOLO_LO_MIO ? 'Vos no cambiaste la hora de corte. Tu permiso ' +
          '<b>auditoria.ver</b> alcanza sólo lo tuyo, así que esto no dice ' +
          'que no la haya cambiado nadie.'
      : 'Todavía nadie cambió la hora de corte'}</td></tr>`;
  if (ERROR_HIST) aviso('#histCorteMsg','bad', ERROR_HIST);
}

// ---------------------------------------------------------------------
//  Guardar
// ---------------------------------------------------------------------
$('#formCorte').onsubmit = async e => {
  e.preventDefault();
  limpiar('#corteMsg');
  const hora = $('#ctHora').value.trim();
  if (!hora){
    aviso('#corteMsg','bad','Escribí la hora, o usá el botón de abajo si lo que querés es que la empresa no tenga corte.');
    return;
  }
  await guardarCorte(hora, $('#ctFrena').checked, $('#ctMotivo').value.trim() || null);
};

//  Quitar el corte (D63) cambia cómo trabaja toda la empresa: va aparte y
//  con confirmación, no con el botón de corregir un minuto.
$('#btnQuitarCorte').onclick = () => {
  limpiar('#corteMsg');
  if (document.getElementById('confQuitar')) return;
  const div = document.createElement('div');
  div.id = 'confQuitar';
  div.innerHTML = `
    <div class="confirmar">
      <div class="conf-tit">Quitar la hora de corte</div>
      <p class="conf-txt">La empresa deja de tener corte: se va a poder cargar y anular
         a cualquier hora, todos los días, sin pedirle autorización a nadie. Se puede
         volver a poner cuando quieras.</p>
      <div class="fila-acc">
        <button class="b b-sec" id="qcCancelar">Dejarlo como está</button>
        <button class="b b-peligro" id="qcOk">Quitar el corte</button>
      </div>
    </div>`;
  $('#corteMsg').after(div);
  $('#qcCancelar', div).onclick = () => div.remove();
  $('#qcOk', div).onclick = async () => {
    div.remove();
    await guardarCorte('', null, $('#ctMotivo').value.trim() || null);
  };
};

//  Ojo: el botón se libera al final, con la recarga hecha (si no, se puede
//  mandar un segundo `fijar_corte`); el `finally` evita que quede trabado.
async function guardarCorte(hora, frena, motivo){
  const btn = $('#ctOk'); btn.disabled = true;
  try {
    const { data, error } = await rpc('fijar_corte', {
      p_hora: hora, p_frena_carga: frena, p_motivo: motivo });
    if (error){ aviso('#corteMsg','bad', error.message); return; }

    //  Primero se recarga, después se avisa: si no, el repintado pisa el aviso.
    $('#ctMotivo').value = '';
    await cargarAjustes();
    //  La advertencia no es un error (el cambio se hizo) pero no puede perderse
    //  abajo del "listo". avisoHtml: son dos párrafos, así que los textos de
    //  la base van escapados a mano.
    avisoHtml('#corteMsg', data.advertencia ? 'warn' : 'ok',
      esc(data.mensaje) + (data.advertencia ? `<br><br><b>${esc(data.advertencia)}</b>` : ''));
  } finally { btn.disabled = false; }
}

// =====================================================================
//  QUIÉN VE LA PLATA DEL NEGOCIO: ya no está (0115, D238)
// =====================================================================
//  La casilla (0051, D117; 0071, D164; 0090, D207; 0110, D234) decidía si el
//  admin global veía la plata. Desde la 0115 la tiene siempre, otorgada, y la
//  tarjeta se fue: un interruptor que no cambia nada mentiría.

